import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

const SESSION_ID_KEY = 'vm_survey_session_id';
const COMPLETED_KEY = 'vm_survey_completed';

/** Show the chip after 1 chat message sent OR 1 ballot contest viewed */
const MESSAGE_THRESHOLD = 1;
const BALLOT_THRESHOLD = 1;

interface SurveyContextValue {
  /** Stable session ID for the current browser session */
  sessionId: string;
  /** Cumulative count of chat messages the user has sent */
  messagesSent: number;
  /** True after the user has submitted the survey this session */
  surveyCompleted: boolean;
  /** True after the user has opened the survey modal (removes red dot) */
  chipOpened: boolean;
  /** Derived: chip should be rendered when threshold met and survey not yet done */
  showChip: boolean;
  /** Call each time the user sends a chat message */
  trackMessageSent: () => void;
  /** Call each time a ballot accordion is expanded by the user */
  trackBallotItemViewed: () => void;
  /** Call when Typeform reports a successful submission */
  markSurveyCompleted: () => void;
  /** Call when the user first opens the survey chip modal */
  markChipOpened: () => void;
  /** Whether the floating survey panel is currently open */
  panelOpen: boolean;
  /** Open the floating survey panel from any component */
  openSurveyPanel: () => void;
  /** Close the floating survey panel */
  closeSurveyPanel: () => void;
}

const SurveyContext = createContext<SurveyContextValue | undefined>(undefined);

export const SurveyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionId] = useState<string>(() => {
    try {
      const existing = sessionStorage.getItem(SESSION_ID_KEY);
      if (existing) return existing;
      const id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_KEY, id);
      return id;
    } catch {
      return crypto.randomUUID();
    }
  });

  const [messagesSent, setMessagesSent] = useState(0);
  const [ballotItemsViewed, setBallotItemsViewed] = useState(0);

  const [surveyCompleted, setSurveyCompleted] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(COMPLETED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [chipOpened, setChipOpened] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const trackMessageSent = useCallback(() => {
    setMessagesSent((prev) => prev + 1);
  }, []);

  const trackBallotItemViewed = useCallback(() => {
    setBallotItemsViewed((prev) => prev + 1);
  }, []);

  const markSurveyCompleted = useCallback(() => {
    try {
      sessionStorage.setItem(COMPLETED_KEY, 'true');
    } catch {
      // sessionStorage unavailable — state-only tracking is fine
    }
    setSurveyCompleted(true);
  }, []);

  const markChipOpened = useCallback(() => {
    setChipOpened(true);
  }, []);

  const openSurveyPanel = useCallback(() => {
    setPanelOpen(true);
  }, []);

  const closeSurveyPanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  const showChip =
    (messagesSent >= MESSAGE_THRESHOLD || ballotItemsViewed >= BALLOT_THRESHOLD) &&
    !surveyCompleted;

  return (
    <SurveyContext.Provider
      value={{
        sessionId,
        messagesSent,
        surveyCompleted,
        chipOpened,
        showChip,
        trackMessageSent,
        trackBallotItemViewed,
        markSurveyCompleted,
        markChipOpened,
        panelOpen,
        openSurveyPanel,
        closeSurveyPanel,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};

export const useSurveyContext = (): SurveyContextValue => {
  const ctx = useContext(SurveyContext);
  if (!ctx) throw new Error('useSurveyContext must be used within SurveyProvider');
  return ctx;
};
